import { useState } from "react";

import PencilSquareIcon from "@heroicons/react/20/solid/PencilSquareIcon";
import { SliceComponentProps } from "@prismicio/react";
import _get from "lodash/get";

import AvatarPreview from "components/avatarPreview/AvatarPreview";
import TertiaryButton from "components/button/tertiaryButton/TertiaryButton";
import SchemaFormModal from "components/modal/schemaFormModal/SchemaFormModal";
import { AvatarsEditFieldSlice, FormDocument } from "prismicio-types";
import { useGetCustomisation, useGetProduct } from "queries/product-queries";
import { FieldSliceContext } from "types/schema";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import { useFindByKey } from "utils/schema-utils";
import { useGetPathnameForDestination } from "utils/url-utils";

import styles from "./AvatarsEditField.module.scss";

export default function AvatarsEditField({
  slice,
}: SliceComponentProps<AvatarsEditFieldSlice, FieldSliceContext>) {
  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();

  const getPathnameForDestination = useGetPathnameForDestination();
  const findByKey = useFindByKey();
  const interpolate = useInterpolate();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const [editAvatarModal, setEditAvatarModal] = useState<FormDocument | null>(
    null
  );

  const characters = slice.items.map(({ muse_schema_key: schemaKey }, i) => {
    if (!schemaKey) {
      return null;
    }

    const field = findByKey(schemaKey);

    if (!field) {
      return null;
    }

    if (!("characterTypeIndex" in field) || !("characterIndex" in field)) {
      throw new Error(
        `Avatar role "${
          schemaKey.split(".")[1] || schemaKey
        }" does not exist in the schema`
      );
    }

    const customisations: any = _get(
      customisation,
      `characters.${field.characterTypeIndex}.customisations.${field.characterIndex}`
    );

    return (
      <button
        key={schemaKey}
        className={styles.avatar}
        type="button"
        onClick={() =>
          setEditAvatarModal(slice.items[i].form as unknown as FormDocument)
        }
      >
        <div className={styles.avatarPreview}>
          <AvatarPreview
            customisations={customisations}
            schemaKey={schemaKey}
          />
        </div>

        <div className={styles.avatarDetails}>
          {customisations?.name && (
            <p className={styles.avatarName}>{customisations.name}</p>
          )}

          <div className={styles.editAvatar}>
            <PencilSquareIcon className={styles.editAvatarIcon} />
            <span>
              {interpolate(slice.primary.edit_button_label || "Edit character")}
            </span>
          </div>
        </div>
      </button>
    );
  });

  return (
    <>
      <TertiaryButton
        as="a"
        color="green"
        href={getPathnameForDestination({
          path: "/create/start",
          query: {
            ...customisation,
            characters: "",
          },
        })}
      >
        {interpolate(
          product.content.canvas.start_again_button_label ||
            CANVAS_FALLBACK_CONTENT.start_again_button_label
        )}
      </TertiaryButton>

      {characters}

      {!!editAvatarModal && (
        <SchemaFormModal
          className={styles.avatarModal}
          formDocument={editAvatarModal}
          isOpen={!!editAvatarModal}
          onDismiss={() => setEditAvatarModal(null)}
          title="Edit"
        />
      )}
    </>
  );
}
